import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tag, X, Check } from "lucide-react";

export function CouponField({ product, quantity, onChange }) {
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: c, product, quantity });
      setApplied(data);
      onChange?.(data);
      toast.success(`Coupon applied — ${data.label}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setApplied(null);
      onChange?.(null);
      toast.error(typeof d === "string" ? d : "Invalid coupon code");
    } finally {
      setLoading(false);
    }
  };

  const remove = () => { setApplied(null); setCode(""); onChange?.(null); };

  if (applied) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="coupon-applied">
        <span className="inline-flex items-center gap-1.5 text-neon border border-neon/30 rounded-full px-3 py-1 font-mono">
          <Check className="w-3.5 h-3.5" aria-hidden="true" /> {applied.code} · {applied.label}
        </span>
        <button onClick={remove} data-testid="coupon-remove-button" aria-label="Remove coupon" className="text-zinc-400 hover:text-red-500 transition-colors">
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Tag className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          placeholder="Coupon code"
          data-testid="coupon-input"
          className="h-11 w-44 pl-9 bg-white border-[#E4EEEC] focus:border-neon rounded-xl font-mono text-ink uppercase placeholder:normal-case"
        />
      </div>
      <Button onClick={apply} disabled={loading || !code.trim()} variant="outline" data-testid="coupon-apply-button"
        className="h-11 rounded-xl border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon">
        {loading ? "…" : "Apply"}
      </Button>
    </div>
  );
}
